import { useTheme } from 'styled-components'
import { IAssignUsersToTeamResponse } from 'features/operationalManagement/operationalManagement.types'
import { Button } from 'styling/GlobalStyles'
import {
  FinalSummary,
  Panel,
  PanelSub,
  PanelTitle,
  ResultRow,
  ResultsColumns,
  ResultsHero,
  ResultsIcon,
  ResultList,
  SummaryChipLabel,
} from '../BulkAssign.styled'

interface Props {
  batchId: string
  targetTeamName: string
  executedAt: Date
  result: IAssignUsersToTeamResponse | null
  onNewBatch: () => void
}

const fmtTime = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
const fmtDate = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`

export const ResultsStep = ({
  batchId,
  targetTeamName,
  executedAt,
  result,
  onNewBatch,
}: Props) => {
  const theme = useTheme() as any
  const successes = result?.successfulAssignments ?? []
  const failures = result?.unsuccessfulAssignments ?? []

  return (
    <>
      <ResultsHero>
        <ResultsIcon>✓</ResultsIcon>
        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>Batch Execution Complete</div>
        <div style={{ color: theme.greyText, fontSize: '0.875rem', marginTop: 4 }}>
          Batch {batchId} processed at {fmtTime(executedAt)}
        </div>
      </ResultsHero>

      <ResultsColumns>
        <Panel accentColor={theme.colorGreen}>
          <PanelTitle style={{ color: theme.colorGreen }}>Successful</PanelTitle>
          <PanelSub>{successes.length} assignment records created</PanelSub>
          <ResultList>
            {successes.length === 0 ? (
              <ResultRow>—</ResultRow>
            ) : (
              successes.map((s) => (
                <ResultRow key={s.id}>
                  <span>
                    {s.user.firstName} {s.user.lastName}
                  </span>
                  <span style={{ color: theme.greyText }}>
                    EMP-{String(s.user.id).padStart(3, '0')}
                  </span>
                </ResultRow>
              ))
            )}
          </ResultList>
        </Panel>

        <Panel accentColor={theme.colorRed}>
          <PanelTitle style={{ color: theme.colorRed }}>Failed</PanelTitle>
          <PanelSub>{failures.length} could not be assigned</PanelSub>
          <ResultList>
            {failures.length === 0 ? (
              <ResultRow>—</ResultRow>
            ) : (
              failures.map((f, i) => (
                <ResultRow key={`${f.user.id}-${i}`}>
                  <span>
                    {f.user.firstName} {f.user.lastName}
                  </span>
                  <span style={{ color: theme.greyText }}>
                    EMP-{String(f.user.id).padStart(3, '0')}
                  </span>
                </ResultRow>
              ))
            )}
          </ResultList>
        </Panel>
      </ResultsColumns>

      <FinalSummary>
        <div>
          <SummaryChipLabel>BATCH ID</SummaryChipLabel>
          <div style={{ fontWeight: 600 }}>{batchId}</div>
        </div>
        <div>
          <SummaryChipLabel>TARGET</SummaryChipLabel>
          <div style={{ fontWeight: 600 }}>{targetTeamName || '—'}</div>
        </div>
        <div>
          <SummaryChipLabel>EMPLOYEES</SummaryChipLabel>
          <div style={{ fontWeight: 600 }}>{successes.length + failures.length}</div>
        </div>
        <div>
          <SummaryChipLabel>EXECUTED</SummaryChipLabel>
          <div style={{ fontWeight: 600 }}>{fmtDate(executedAt)}</div>
        </div>
      </FinalSummary>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button orange onClick={onNewBatch}>
          + New batch assignment
        </Button>
      </div>
    </>
  )
}
